import React, { useState } from 'react';
import { RoundingList } from './RoundingList';
import { RoundingForm } from './RoundingForm';
import { BookIcon, ChevronRightIcon, CircleDotIcon, PlusIcon, EditIcon, TrashIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
import type { OrgUnit } from '../sharedFinancialData';
export type RoundingScope = 'Global' | 'OrgUnit' | 'Vendor';
export type RoundingRule = {
  id: string;
  name: string;
  description: string;
  roundingType: 'Nearest' | 'Up' | 'Down' | 'Swedish' | 'Truncate';
  interval: number;
  scope: RoundingScope;
  scopeId?: string;
  scopeName?: string;
  scopePath?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};
export function RoundingConfiguration({
  locale = 'en-US'
}) {
  const [view, setView] = useState<'list' | 'form'>('list');
  const [editingRule, setEditingRule] = useState<RoundingRule | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [roundingRules, setRoundingRules] = useState<RoundingRule[]>([{
    id: 'round-001',
    name: 'Standard Cents',
    description: 'Round to nearest cent (0.01)',
    roundingType: 'Nearest',
    interval: 0.01,
    scope: 'Global',
    active: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'round-002',
    name: 'Nickel Rounding',
    description: 'Round up to nearest 0.05',
    roundingType: 'Up',
    interval: 0.05,
    scope: 'OrgUnit',
    scopeId: 'ou-apac-sg',
    scopeName: 'Singapore Office',
    scopePath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    active: true,
    createdAt: '2023-01-15T00:00:00Z',
    updatedAt: '2023-01-15T00:00:00Z'
  }, {
    id: 'round-003',
    name: 'Whole Dollar',
    description: 'Round down to nearest dollar',
    roundingType: 'Down',
    interval: 1.0,
    scope: 'OrgUnit',
    scopeId: 'ou-latam-mx',
    scopeName: 'Mexico Office',
    scopePath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    active: true,
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z'
  }, {
    id: 'round-004',
    name: 'Swedish Cash Rounding',
    description: 'Swedish rounding for cash transactions',
    roundingType: 'Swedish',
    interval: 0.5,
    scope: 'Global',
    active: false,
    createdAt: '2023-02-15T00:00:00Z',
    updatedAt: '2023-03-01T00:00:00Z'
  }, {
    id: 'round-005',
    name: 'Vendor Precision Rounding',
    description: 'High precision rounding for energy supplier',
    roundingType: 'Nearest',
    interval: 0.0001,
    scope: 'Vendor',
    scopeId: 'vendor-001',
    scopeName: 'Office Depot',
    active: true,
    createdAt: '2023-03-10T00:00:00Z',
    updatedAt: '2023-03-10T00:00:00Z'
  }, {
    id: 'round-006',
    name: 'Dell Rounding',
    description: 'Round up for IT equipment vendor',
    roundingType: 'Up',
    interval: 0.01,
    scope: 'Vendor',
    scopeId: 'vendor-002',
    scopeName: 'Dell Technologies',
    active: true,
    createdAt: '2023-04-01T00:00:00Z',
    updatedAt: '2023-04-01T00:00:00Z'
  }]);
  const handleAddRule = () => {
    setEditingRule(null);
    setView('form');
  };
  const handleEditRule = (rule: RoundingRule) => {
    setEditingRule(rule);
    setView('form');
  };
  const handleSaveRule = (rule: Omit<RoundingRule, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingRule) {
      setRoundingRules(roundingRules.map(r => r.id === editingRule.id ? {
        ...editingRule,
        ...rule,
        updatedAt: new Date().toISOString()
      } : r));
    } else {
      const newRule: RoundingRule = {
        id: `round-${Date.now()}`,
        ...rule,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setRoundingRules([...roundingRules, newRule]);
    }
    setView('list');
    setEditingRule(null);
  };
  const handleToggleActive = (id: string) => {
    setRoundingRules(roundingRules.map(rule => rule.id === id ? {
      ...rule,
      active: !rule.active,
      updatedAt: new Date().toISOString()
    } : rule));
  };
  const handleDeleteRule = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setRoundingRules(roundingRules.filter(rule => rule.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  const handleCancelForm = () => {
    setView('list');
    setEditingRule(null);
  };
  const globalRules = roundingRules.filter(r => r.scope === 'Global');
  const vendorRules = roundingRules.filter(r => r.scope === 'Vendor');
  const orgUnitRules = roundingRules.filter(r => r.scope === 'OrgUnit');
  return <div data-id="element-10911">
      <div className="mb-8" data-id="element-10912">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-10913">
          Rounding Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-10914">
          Define rounding rules at global, vendor, and organisation unit levels.
          Resolution follows override hierarchy: Vendor → OrgUnit → Global.
        </p>
      </div>

      {view === 'list' ? <>
          {/* Global Rules */}
          <div className="bg-white shadow rounded-lg mb-6" data-id="element-10915">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10916">
              <div className="flex items-center justify-between" data-id="element-10917">
                <div data-id="element-10918">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10919">
                    Global Rounding Rules
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-10920">
                    Default rounding rules applied across the entire system
                  </p>
                </div>
                <button onClick={handleAddRule} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10921">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-10922" />
                  Add Rule
                </button>
              </div>
            </div>

            {globalRules.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-10923">
                <p className="text-sm text-gray-500" data-id="element-10924">
                  No global rounding rules configured.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-10925">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-10926">
                  <thead className="bg-gray-50" data-id="element-10927">
                    <tr data-id="element-10928">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10929">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10930">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10931">
                        Interval
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10932">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10933">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-10934">
                    {globalRules.map(rule => <tr key={rule.id} data-id="element-10935">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10936">
                          <div className="text-sm font-medium text-gray-900" data-id="element-10937">
                            {rule.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-10938">
                            {rule.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10939">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-10940">
                            {rule.roundingType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10941">
                          {rule.interval}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10942">
                          {rule.active ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-10943">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-10944" />
                              Active
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10945">
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10946">
                          <button onClick={() => handleToggleActive(rule.id)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-10947">
                            {rule.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleEditRule(rule)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-10948">
                            <EditIcon className="h-4 w-4 inline" data-id="element-10949" />
                          </button>
                          <button onClick={() => handleDeleteRule(rule.id)} className="text-red-600 hover:text-red-900" data-id="element-10950">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-10951" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* OrgUnit Rules */}
          <div className="bg-white shadow rounded-lg mb-6" data-id="element-10952">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10953">
              <div className="flex items-center justify-between" data-id="element-10954">
                <div data-id="element-10955">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10956">
                    Organisation Unit Rules
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-10957">
                    Rounding rules for specific organisation units (overrides
                    Global)
                  </p>
                </div>
              </div>
            </div>

            {orgUnitRules.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-10958">
                <p className="text-sm text-gray-500" data-id="element-10959">
                  No organisation unit-specific rounding rules configured.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-10960">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-10961">
                  <thead className="bg-gray-50" data-id="element-10962">
                    <tr data-id="element-10963">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10964">
                        Organisation Unit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10965">
                        Rule Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10966">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10967">
                        Interval
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10968">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10969">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-10970">
                    {orgUnitRules.map(rule => <tr key={rule.id} data-id="element-10971">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10972">
                          <div className="text-sm font-medium text-gray-900" data-id="element-10973">
                            {rule.scopeName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-10974">
                            {rule.scopePath}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10975">
                          <div className="text-sm font-medium text-gray-900" data-id="element-10976">
                            {rule.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-10977">
                            {rule.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10978">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-10979">
                            {rule.roundingType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10980">
                          {rule.interval}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10981">
                          {rule.active ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-10982">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-10983" />
                              Active
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10984">
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10985">
                          <button onClick={() => handleToggleActive(rule.id)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-10986">
                            {rule.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleEditRule(rule)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-10987">
                            <EditIcon className="h-4 w-4 inline" data-id="element-10988" />
                          </button>
                          <button onClick={() => handleDeleteRule(rule.id)} className="text-red-600 hover:text-red-900" data-id="element-10989">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-10990" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* Vendor-Specific Rules */}
          <div className="bg-white shadow rounded-lg" data-id="element-10991">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10992">
              <div className="flex items-center justify-between" data-id="element-10993">
                <div data-id="element-10994">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10995">
                    Vendor-Specific Rules
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-10996">
                    Rounding rules for specific vendors (overrides Global and
                    OrgUnit)
                  </p>
                </div>
              </div>
            </div>

            {vendorRules.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-10997">
                <p className="text-sm text-gray-500" data-id="element-10998">
                  No vendor-specific rounding rules configured.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-10999">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-11000">
                  <thead className="bg-gray-50" data-id="element-11001">
                    <tr data-id="element-11002">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11003">
                        Vendor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11004">
                        Rule Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11005">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11006">
                        Interval
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11007">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11008">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-11009">
                    {vendorRules.map(rule => <tr key={rule.id} data-id="element-11010">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-11011">
                          {rule.scopeName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-11012">
                          <div className="text-sm font-medium text-gray-900" data-id="element-11013">
                            {rule.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-11014">
                            {rule.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-11015">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-11016">
                            {rule.roundingType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11017">
                          {rule.interval}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-11018">
                          {rule.active ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-11019">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-11020" />
                              Active
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-11021">
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11022">
                          <button onClick={() => handleToggleActive(rule.id)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-11023">
                            {rule.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleEditRule(rule)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-11024">
                            <EditIcon className="h-4 w-4 inline" data-id="element-11025" />
                          </button>
                          <button onClick={() => handleDeleteRule(rule.id)} className="text-red-600 hover:text-red-900" data-id="element-11026">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-11027" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
        </> : <RoundingForm rule={editingRule} onSave={handleSaveRule} onCancel={handleCancelForm} data-id="element-11028" />}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-11029">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-11030">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-11031">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-11032"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-11033">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-11034">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-11035">
                <div className="sm:flex sm:items-start" data-id="element-11036">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-11037">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-11038" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-11039">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11040">
                      Delete Rounding Rule
                    </h3>
                    <div className="mt-2" data-id="element-11041">
                      <p className="text-sm text-gray-500" data-id="element-11042">
                        Are you sure you want to delete this rounding rule? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-11043">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-11044">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-11045">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}